import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Building2,
  Home,
  Check,
  X,
} from 'lucide-react';

const PRODUCTION_PRESETS = [
  {
    id: 'multifamily',
    name: 'Multifamily (MF 5+)',
    description: 'Standard multifamily properties with 5+ units',
    category: 'scope',
    icon: <Building2 size={16} />,
    filters: { propertyType: 'mf5+' },
    propertyTypes: ['mf50-99', 'mf100-199', 'mf200', 'duplex', 'sfr'],
    layers: { rent_heat: true, pipeline: true, inventory: true },
    cards: {
      priority: [
        'rent-by-bed',
        'rent-distribution',
        'concessions',
        'inventory-pipeline',
        'demographics',
      ],
    },
    benchmarks: ['zip', 'city', 'ring_3'],
  },
  {
    id: 'large-mf',
    name: 'Large MF (100+ units)',
    description: 'Large multifamily properties with 100+ units, newer vintage',
    category: 'scope',
    icon: <Building2 size={16} />,
    filters: {
      propertyType: 'mf5+',
      unitCount: '100+',
      vintage: '2000+',
      managed: true,
    },
    propertyTypes: ['mf100-199', 'mf200'],
    layers: { rent_heat: true, pipeline: true, concessions: true },
    cards: {
      priority: [
        'rent-by-bed',
        'lease-up-velocity',
        'concessions',
        'occupancy',
        'unit-mix',
      ],
    },
    benchmarks: ['zip', 'msa', 'ring_3'],
  },
  {
    id: 'sfr-btr',
    name: 'Single-Family Rentals (SFR/BTR)',
    description: 'Single-family and build-to-rent properties',
    category: 'scope',
    icon: <Home size={16} />,
    filters: { propertyType: 'sfr/btr' },
    propertyTypes: ['mf5-19', 'sfr'],
    layers: { rent_heat: true, schools: true, walkability: true },
    cards: {
      priority: [
        'rent-vs-sqft',
        'rent-distribution',
        'demographics',
        'schools',
        'comps-table',
      ],
    },
    benchmarks: ['zip', 'city', 'ring_5'],
  },
];

const PresetsMenu = ({ onClose, onApplyPreset, activePresets }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const scopePresets = PRODUCTION_PRESETS.filter(p => p.category === 'scope');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Analysis Presets
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                marginTop: '0.25rem',
              }}
            >
              Apply pre-configured filters, layers, and card stacks
            </Typography>
          </Box>
          <IconButton onClick={onClose} size='small'>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                flex: 1,
                color: '#39585C',
                '&.Mui-selected': {
                  color: '#39585C',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#39585C',
              },
            }}
          >
            <Tab
              label={`Property Scope (${scopePresets.length})`}
              sx={{
                textTransform: 'none',
                flex: 1,
                color: '#39585C',
                '&.Mui-selected': {
                  color: '#39585C',
                },
              }}
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: { xs: '1rem', md: '1.5rem' },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: '1rem',
            }}
          >
            {scopePresets.map(preset => (
              <PresetCard
                key={preset.id}
                preset={preset}
                isActive={activePresets.includes(preset.id)}
                onApply={() => onApplyPreset(preset)}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#39585C',
            borderColor: '#39585C',
            '&:hover': {
              borderColor: '#2d4649',
              backgroundColor: '#F0F4F5',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function PresetCard({ preset, isActive, onApply }) {
  return (
    <Card
      onClick={onApply}
      sx={{
        padding: '1rem',
        cursor: 'pointer',
        border: isActive ? '2px solid #39585C' : '1px solid #E5E7EB',
        backgroundColor: isActive ? '#F0F4F5' : '#FFFFFF',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
        <Box
          sx={{
            padding: '0.5rem',
            borderRadius: '0.5rem',
            backgroundColor: isActive ? '#39585C' : '#F3F4F6',
            color: isActive ? '#FFFFFF' : '#6B7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {preset.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: '0.5rem',
              marginBottom: '0.25rem',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                color: '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {preset.name}
            </Typography>
            {isActive && (
              <Check size={16} color='#39585C' style={{ flexShrink: 0 }} />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: { xs: '0.625rem', md: '0.75rem' },
              color: '#6B7280',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {preset.description}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

export default PresetsMenu;
